import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";

const STORAGE_KEY = "fm-brand-mode";

const BRAND_META: Record<string, { name: string; shortName: string; color: string; tailwindBorder: string; tailwindText: string; tailwindBg: string }> = {
  "sustainable-engineering-alliance": { name: "Sustainable Engineering Alliance", shortName: "SEA", color: "emerald", tailwindBorder: "border-emerald-500", tailwindText: "text-emerald-600", tailwindBg: "bg-emerald-500" },
  "disti": { name: "Disti", shortName: "Disti", color: "blue", tailwindBorder: "border-blue-500", tailwindText: "text-blue-600", tailwindBg: "bg-blue-500" },
  "devkit": { name: "Devkit", shortName: "Devkit", color: "violet", tailwindBorder: "border-violet-500", tailwindText: "text-violet-600", tailwindBg: "bg-violet-500" },
};

export type BrandMeta = typeof BRAND_META[string];

interface BrandContextValue {
  activeBrand: string | null;
  activeBrandMeta: BrandMeta | null;
  setBrand: (slug: string | null) => void;
}

const BrandContext = createContext<BrandContextValue>({
  activeBrand: null,
  activeBrandMeta: null,
  setBrand: () => {},
});

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();

  const getInitialBrand = (): string | null => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("brand");
    if (fromUrl && BRAND_META[fromUrl]) return fromUrl;
    try {
      const fromStorage = localStorage.getItem(STORAGE_KEY);
      if (fromStorage && BRAND_META[fromStorage]) return fromStorage;
    } catch { /* ignore */ }
    return null;
  };

  const [activeBrand, setActiveBrand] = useState<string | null>(getInitialBrand);

  const setBrand = useCallback((slug: string | null) => {
    setActiveBrand(slug);
    try {
      if (slug) localStorage.setItem(STORAGE_KEY, slug);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    const url = new URL(window.location.href);
    if (slug) url.searchParams.set("brand", slug);
    else url.searchParams.delete("brand");
    window.history.replaceState({}, "", url.toString());
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("brand");
    if (fromUrl && BRAND_META[fromUrl] && fromUrl !== activeBrand) {
      setActiveBrand(fromUrl);
      try { localStorage.setItem(STORAGE_KEY, fromUrl); } catch { /* ignore */ }
    }
  }, [location]);

  const activeBrandMeta = activeBrand ? (BRAND_META[activeBrand] ?? null) : null;

  return (
    <BrandContext.Provider value={{ activeBrand, activeBrandMeta, setBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}

export { BRAND_META };
